import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaBook, FaClipboardList, FaTimesCircle } from "react-icons/fa";
import { CustomButton, CustomTextInput } from "../../../../components";
import QuillEditor from "../../../../components/QuillEditor/QuillEditor";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import academicStyles from "../../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import viewStyles from "../../../../pages/student/book/assessment/Viewassessment.module.css";
import styles from "../assessment/Assessmentcreate.module.css";

const OPTION_LETTERS = ["a", "b", "c", "d", "e", "f"];
const MAX_REGENERATE = 3;

const isQuillContentEmpty = (html) => {
  if (!html || typeof html !== "string") return true;
  const stripped = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return stripped.length === 0;
};

function StudyMaterialCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, translations } = useSelector((state) => state.language || {});

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};

  const school_id = clientData?.school_id || "";
  const user_id = user?.id || clientData?.master_id || "";
  const assessment_id = location?.state?.assessment_id || "";
  const student_id = location?.state?.student_id || "";
  const routeData = location?.state?.data || {};

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [assessment, setAssessment] = useState({});
  const [wrongQuestions, setWrongQuestions] = useState([]);
  const [studyMaterialContent, setStudyMaterialContent] = useState("");
  const [regenerateCount, setRegenerateCount] = useState(0);

  // Info-only modal (success / failure messages).
  const [modal, setModal] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  const canRegenerate = regenerateCount < MAX_REGENERATE;

  const viewPayload = useMemo(
    () => ({
      school_id,
      assessment_id,
      student_id,
    }),
    [school_id, assessment_id, student_id],
  );

  const showInfoModal = (type, message, redirectPath = "") => {
    setModal({ open: true, type, message, redirectPath });
  };

  const closeModal = () => {
    setModal({ open: false, type: "", message: "", redirectPath: "" });
  };

  // ────────────────────────────────────────────────────────────────────────
  // OK on the modal — close, then redirect if a redirectPath was attached.
  // Supports string paths and { path, state, replace } objects.
  // ────────────────────────────────────────────────────────────────────────
  const handleSuccessOk = () => {
    const { redirectPath } = modal;
    const pathToNavigate = redirectPath;
    setModal({ open: false, type: "", message: "", redirectPath: "" });
    if (pathToNavigate) {
      if (typeof pathToNavigate === "object" && pathToNavigate.path) {
        navigate(pathToNavigate.path, {
          state: pathToNavigate.state,
          replace: pathToNavigate.replace || false,
        });
      } else if (
        typeof pathToNavigate === "string" &&
        pathToNavigate.trim() !== ""
      ) {
        navigate(pathToNavigate);
      }
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Fetch assessment + wrong questions
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchStudyMaterialView = async () => {
      if (
        !viewPayload.school_id ||
        !viewPayload.assessment_id ||
        !viewPayload.student_id
      ) {
        return;
      }

      setLoading(true);
      try {
        const response = await AxiosInstance.post(
          "/book/createStudyMaterial",
          viewPayload,
        );

        const respData = response?.data || {};

        if (respData?.status) {
          const data = respData?.data || {};
          setAssessment(data?.assessment || {});
          setWrongQuestions(
            Array.isArray(data?.wrong_questions) ? data.wrong_questions : [],
          );
          setStudyMaterialContent(data?.content || "");
        } else if (respData?.already_exists === true) {
          showInfoModal(
            "Failed",
            respData?.message ||
              t?.studyMaterialAlreadyGenerated ||
              "Study material already generated for this student",
          );
        } else {
          showInfoModal(
            "Failed",
            respData?.message ||
              t?.failedToFetchAssessment ||
              "Failed to fetch assessment details",
          );
        }
      } catch (error) {
        showInfoModal(
          "Failed",
          error?.response?.data?.message ||
            t?.failedToFetchAssessment ||
            "Failed to fetch assessment details",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStudyMaterialView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPayload, t?.failedToFetchAssessment]);

  // ────────────────────────────────────────────────────────────────────────
  // Generate
  // ────────────────────────────────────────────────────────────────────────
  const handleGenerateStudyMaterial = async () => {
    if (
      !school_id ||
      !assessment_id ||
      !student_id ||
      !user_id ||
      !canRegenerate
    )
      return;

    setGenerating(true);
    try {
      const payload = {
        school_id,
        assessment_id,
        student_id,
        user_id: user_id,
      };

      const response = await AxiosInstance.post(
        "/book/generateStudyMaterial",
        payload,
      );
      const data = response?.data || {};
      const isSuccess =
        data?.status === true ||
        data?.success === true ||
        (typeof data?.content === "string" && data.content.trim() !== "");

      if (isSuccess) {
        const generatedContent = data?.content || "";

        setStudyMaterialContent(generatedContent);
        setRegenerateCount((prev) => prev + 1);

        showInfoModal(
          "success",
          data?.message ||
            t?.assessmentGeneratedSuccess ||
            "Study material generated successfully",
        );
      } else if (data?.already_exists === true) {
        showInfoModal(
          "Failed",
          data?.message ||
            t?.studyMaterialAlreadyGenerated ||
            "Study material already generated for this student",
        );
      } else {
        showInfoModal(
          "Failed",
          data?.message ||
            t?.failedToGenerateAssessment ||
            "Failed to generate study material. Please try again.",
        );
      }
    } catch (error) {
      showInfoModal(
        "Failed",
        error?.response?.data?.message ||
          t?.failedToGenerateAssessment ||
          "Failed to generate study material",
      );
    } finally {
      setGenerating(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Submit — on success, attach redirectPath so OK on the modal
  // navigates back to /user/assessmentView with { id, data }.
  // ────────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (isQuillContentEmpty(studyMaterialContent)) {
      showInfoModal(
        "Failed",
        t?.somethingWentWrong ||
          "Please generate study material before submitting",
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        school_id,
        assessment_id,
        student_id,
        user_id: user_id,
        content: studyMaterialContent,
        regeneration_count: regenerateCount,
      };

      const response = await AxiosInstance.post(
        "/book/storeStudyMaterial",
        payload,
      );

      if (response?.data?.status === true || response?.data?.success === true) {
        showInfoModal(
          "success",
          response?.data?.message ||
            t?.assessmentSavedSuccess ||
            "Study material submitted successfully",
          {
            path: "/user/assessmentView",
            state: {
              id: assessment_id,
              data: routeData,
            },
          },
        );
      } else {
        showInfoModal(
          "Failed",
          response?.data?.message ||
            t?.failedToSaveAssessment ||
            "Failed to submit study material",
        );
      }
    } catch (error) {
      showInfoModal(
        "Failed",
        error?.response?.data?.message ||
          t?.failedToSaveAssessment ||
          "Failed to submit study material",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Cancel — just go back.
  // ────────────────────────────────────────────────────────────────────────
  const handleCancel = () => {
    navigate(-1);
  };

  const bookDetails = useMemo(
    () => ({
      book: assessment?.book_name || "",
      className: assessment?.class_name || "",
      section: assessment?.section_id || "",
      medium: assessment?.medium || "",
      topic: assessment?.topic || "",
      subTopic: assessment?.sub_topic || "",
      startPage: assessment?.start_page || "",
      endPage: assessment?.end_page || "",
      mcqOptions: assessment?.mcq_options_count || 4,
    }),
    [assessment],
  );

  const modalTitle =
    modal.type === "success"
      ? translations?.labels?.success || t?.success || "Success"
      : translations?.labels?.failed || t?.failed || "Failed";

  const isContentEmpty = isQuillContentEmpty(studyMaterialContent);

  // Button label: "Generate" before first generation, "Re-Generate" after.
  const generateButtonLabel =
    regenerateCount === 0
      ? t?.generate || "Generate"
      : t?.regenerate || "Re-Generate";

  return (
    <div>
      {/* ========== Book Details Card ========== */}
      <div className={academicStyles.container}>
        <div className={academicStyles.header}>
          <div className={academicStyles.headerIcon}>
            <FaBook />
          </div>
          <h1 className={academicStyles.headerTitle}>
            {t?.bookDetails || "Book Details"}
          </h1>
        </div>

        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.book || "Book"}
              value={bookDetails.book}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.class || "Class"}
              value={bookDetails.className}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.section || "Section"}
              value={bookDetails.section}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.medium || "Medium"}
              value={bookDetails.medium}
              disabled
              readOnly
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.topic || "Topic"}
              value={bookDetails.topic}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.subTopic || "Sub Topic"}
              value={bookDetails.subTopic}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.endPageLabel || "End Page"}
              value={bookDetails.endPage}
              disabled
              readOnly
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.startPageLabel || "Start Page"}
              value={bookDetails.startPage}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>

      {/* ========== Assessment Questions + Study Material ========== */}
      <div className={`row mt-3 ${styles.previewRow}`}>
        {/* ===== LEFT: Assessment Questions ===== */}
        <div className={`col-lg-6 col-md-12 px-2 mb-3 ${styles.panelCol}`}>
          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div className={styles.panelHeaderIcon}>
                <FaClipboardList />
              </div>
              <h1 className={styles.panelHeaderTitle}>
                {t?.assessmentQuestions || "Assessment Questions"}
              </h1>
            </div>

            <div className={styles.previewBody}>
              {loading ? (
                <div className={viewStyles.loaderWrapper}>
                  <div className={viewStyles.spinner} />
                  <p>{t?.loading || "Loading..."}</p>
                </div>
              ) : wrongQuestions.length === 0 ? (
                <div className={styles.emptyState}>
                  {t?.noQuestionsGenerated || "No questions available"}
                </div>
              ) : (
                wrongQuestions.map((q, qIdx) => {
                  const optionCount = Number(bookDetails.mcqOptions) || 4;
                  const correct = (q?.correct_answer || "")
                    .toString()
                    .toLowerCase();
                  const studentAnswer = (q?.student_answer || "")
                    .toString()
                    .toLowerCase();

                  return (
                    <div key={q?.id || qIdx} className={styles.questionBlock}>
                      <div className={styles.questionText}>
                        {qIdx + 1}. {q?.assessment_question}
                      </div>

                      <div className={styles.optionsGrid}>
                        {OPTION_LETTERS.slice(0, optionCount).map((letter) => {
                          const optKey = `option_${letter}`;
                          const optValue = q?.[optKey];
                          if (optValue === undefined || optValue === null)
                            return null;

                          const isStudentChoice = studentAnswer === letter;
                          const isChecked = isStudentChoice;

                          let optionCardClass = styles.optionCard;
                          if (isStudentChoice) {
                            optionCardClass = `${styles.optionCard} ${styles.optionCardCorrect}`;
                          }

                          let radioMarkClass = styles.radioMark;
                          const radioInnerClass = styles.radioInner;
                          if (isStudentChoice) {
                            radioMarkClass = `${styles.radioMark} ${styles.radioMarkChecked}`;
                          }

                          return (
                            <div key={letter} className={styles.optionCol}>
                              <div className={optionCardClass}>
                                <span className={styles.radioWrapper}>
                                  <input
                                    type="radio"
                                    name={`q-${qIdx}`}
                                    checked={isChecked}
                                    readOnly
                                    className={styles.radioInput}
                                  />
                                  <span className={radioMarkClass}>
                                    {isChecked && (
                                      <span className={radioInnerClass} />
                                    )}
                                  </span>
                                </span>
                                <span className={styles.optionText}>
                                  {letter}) {optValue}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {(q?.explanation || correct) && (
                        <div
                          className={`${styles.explanationPanel} ${styles.explanationPanelWrong}`}
                        >
                          <FaTimesCircle
                            className={`${styles.explanationIcon} ${styles.explanationIconWrong}`}
                          />
                          <div className={styles.explanationContent}>
                            <div className={styles.explanationCorrectAnswer}>
                              {studentAnswer || correct}){" "}
                              {q?.[`option_${studentAnswer || correct}`] || ""}
                            </div>
                            <div className={styles.explanationText}>
                              {t?.explanation || "Explanation"} :{" "}
                              {q?.explanation || "-"}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ===== RIGHT: Study Material with loader overlay ===== */}
        <div className={`col-lg-6 col-md-12 px-2 mb-3 ${styles.panelCol}`}>
          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div className={styles.panelHeaderIcon}>
                <FaClipboardList />
              </div>
              <h1 className={styles.panelHeaderTitle}>
                {t?.studyMaterial || "Study Material"}
              </h1>
            </div>

            <div className={styles.previewBody}>
              <div style={{ position: "relative" }}>
                <QuillEditor
                  value={studyMaterialContent}
                  onChange={setStudyMaterialContent}
                  placeholder={
                    t?.lessonPlanEditorPlaceholder ||
                    "Study material will appear here after generation..."
                  }
                  minHeight={400}
                  maxHeight={620}
                  readOnly={generating}
                  showWordCount={false}
                />

                {generating && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,0.85)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                      borderRadius: "var(--border-radius-md)",
                      zIndex: 5,
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        border: "3px solid var(--border-light, #e5e7eb)",
                        borderTopColor: "var(--color-primary)",
                        borderRadius: "50%",
                        animation: "sm-spin 0.9s linear infinite",
                      }}
                    />
                    <div
                      style={{
                        color: "var(--text-muted, #6b7280)",
                        fontSize: "var(--font-size-sm, 14px)",
                      }}
                    >
                      {t?.generatingWithAi || "Generating with Gemini AI"}
                    </div>
                    <style>{`@keyframes sm-spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== Action Buttons ========== */}
      <div
        className={academicStyles.actions}
        style={{ justifyContent: "center", marginTop: 8 }}
      >
        <CustomButton
          type="button"
          variant="contained"
          className={academicStyles.cancelButton}
          fullWidth={false}
          onClick={handleCancel}
        >
          {t?.cancel || "CANCEL"}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          className={academicStyles.addButton}
          fullWidth={false}
          onClick={handleGenerateStudyMaterial}
          disabled={generating || !canRegenerate}
        >
          {generating
            ? t?.generating || "Generating..."
            : `${generateButtonLabel} ${regenerateCount}/${MAX_REGENERATE}`}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          className={academicStyles.addButton}
          fullWidth={false}
          onClick={handleSubmit}
          disabled={submitting || generating || isContentEmpty}
        >
          {submitting
            ? t?.submitting || "Submitting..."
            : t?.submit || "SUBMIT"}
        </CustomButton>
      </div>

      {/* ===== Info modal (success / failure) ===== */}
      <PopupModal
        open={modal.open}
        type={modal.type}
        title={modalTitle}
        message={modal.message}
        onOk={handleSuccessOk}
        onClose={closeModal}
      />
    </div>
  );
}

export default StudyMaterialCreate;
